import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, MessageSquare } from "lucide-react";
import { useSendGymMessage } from "@/hooks/gym-messages/useSendGymMessage";
import { useMyGymMessages } from "@/hooks/gym-messages/useMyGymMessages";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

interface GymMessageFormProps {
  gymId: string;
}

const MAX_MESSAGE_LENGTH = 500;

export const GymMessageForm = ({ gymId }: GymMessageFormProps) => {
  const [message, setMessage] = useState("");
  const sendMessage = useSendGymMessage();
  const { data: myMessages } = useMyGymMessages(gymId);

  const handleSendMessage = () => {
    if (!gymId || !message.trim()) return;
    sendMessage.mutate(
      { gymId, content: message.trim() },
      { onSuccess: () => setMessage("") }
    );
  };

  return (
    <div className="space-y-2 mt-3">
      <Label htmlFor={`gymMessage-${gymId}`} className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4" />
        Enviar un mensaje al gimnasio
      </Label>
      <Textarea
        id={`gymMessage-${gymId}`}
        placeholder="Escribe tu mensaje, sugerencia o consulta..."
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
        rows={3}
        className="bg-background resize-none"
        disabled={sendMessage.isPending}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {message.length}/{MAX_MESSAGE_LENGTH}
        </span>
        <Button
          size="sm"
          onClick={handleSendMessage}
          disabled={sendMessage.isPending || message.trim().length === 0}
        >
          <Send className="h-4 w-4 mr-2" />
          {sendMessage.isPending ? "Enviando..." : "Enviar mensaje"}
        </Button>
      </div>

      {myMessages && myMessages.length > 0 && (
        <div className="mt-3 space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Tus últimos mensajes
          </p>
          <div className="space-y-1.5">
            {myMessages.map((m) => (
              <div
                key={m.id}
                className="p-2 rounded-md bg-muted/50 border border-border/50 text-xs"
              >
                <p className="text-foreground line-clamp-2">{m.content}</p>
                <p className="text-muted-foreground mt-1">
                  {formatDistanceToNow(new Date(m.created_at), {
                    addSuffix: true,
                    locale: es,
                  })}
                  {m.is_read && " · Leído"}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
